import React, { useState, useEffect } from 'react'
import { toast } from 'react-toastify'
import { adminSettingsApi } from '../../../services/api'
import { Save, RefreshCw, AlertTriangle, CheckCircle, Landmark, FileCheck, Scale, Building2, FileSignature, Shield, Lock } from 'lucide-react'

interface GovernmentSettings {
  general: {
    workflowMode: string
    defaultPostingWindowDays: number
    minimumBidWindowDays: number
    procurementThreshold: number
    allowEmergencyProcurement: boolean
    publicNoticeRequired: boolean
    bidBondRequired: boolean
    performanceBondRequired: boolean
  }
  vendorVerification: {
    certificationRequired: boolean
    requiredDocuments: string[]
    autoVerificationEnabled: boolean
    verificationTimeoutDays: number
    samRegistrationRequired: boolean
    cageCodeRequired: boolean
    dunsNumberRequired: boolean
    gsaSchedulePreferred: boolean
    securityClearanceRequired: boolean
  }
  procurementRules: {
    bidOpeningPolicy: string
    allowSeatedBids: boolean
    allowElectronicBids: boolean
    tieBreakingMethod: string
    localPreferenceEnabled: boolean
    localPreferencePercentage: number
    smallBusinessPreferenceEnabled: boolean
    smallBusinessPreferencePercentage: number
    veteranOwnedPreferenceEnabled: boolean
    veteranOwnedPreferencePercentage: number
    awardCriteria: {
      price: number
      technicalCapability: number
      pastPerformance: number
      deliveryTime: number
    }
  }
  agencyManagement: {
    multiTierApprovalRequired: boolean
    approvalTiers: number
    agencyTypes: string[]
    contractingOfficerApprovalRequired: boolean
    legalReviewRequired: boolean
    legalReviewThreshold: number
    budgetOfficerApprovalRequired: boolean
    chiefApprovalThreshold: number
  }
  contractTemplates: {
    useStandardTemplates: boolean
    allowCustomClauses: boolean
    requiredClauses: string[]
    forbiddenClauses: string[]
    templateApprovalRequired: boolean
    contractTypeDefaults: {
      fixedPrice: boolean
      costReimbursement: boolean
      timeAndMaterials: boolean
      indefiniteDelivery: boolean
    }
  }
  complianceAudit: {
    auditTrailEnabled: boolean
    detailedLoggingEnabled: boolean
    retentionPeriodYears: number
    quarterlyReportsRequired: boolean
    annualAuditsRequired: boolean
    externalAuditorsAllowed: boolean
    farComplianceChecks: boolean
    whistleblowerProtection: boolean
    fraudDetectionEnabled: boolean
  }
  securityAccess: {
    clearanceLevels: string[]
    documentClassificationEnabled: boolean
    encryptionRequired: boolean
    vpnRequired: boolean
    ipRestrictionEnabled: boolean
    allowedIpRanges: string[]
    twoFactorRequired: boolean
    sessionTimeoutMinutes: number
    maxConcurrentSessions: number
    geoRestrictionEnabled: boolean
    allowedCountries: string[]
  }
  lastModified: string
  lastModifiedBy: string
}

const GovernmentSettingsPanel: React.FC = () => {
  const [settings, setSettings] = useState<GovernmentSettings>({
    general: {
      workflowMode: 'open',
      defaultPostingWindowDays: 30,
      minimumBidWindowDays: 10,
      procurementThreshold: 10000,
      allowEmergencyProcurement: true,
      publicNoticeRequired: true,
      bidBondRequired: false,
      performanceBondRequired: true
    },
    vendorVerification: {
      certificationRequired: true,
      requiredDocuments: ['company_registration', 'tax_id', 'insurance_certificate'],
      autoVerificationEnabled: false,
      verificationTimeoutDays: 5,
      samRegistrationRequired: true,
      cageCodeRequired: true,
      dunsNumberRequired: true,
      gsaSchedulePreferred: true,
      securityClearanceRequired: false
    },
    procurementRules: {
      bidOpeningPolicy: 'scheduled',
      allowSeatedBids: true,
      allowElectronicBids: true,
      tieBreakingMethod: 'lowest-price',
      localPreferenceEnabled: false,
      localPreferencePercentage: 5,
      smallBusinessPreferenceEnabled: true,
      smallBusinessPreferencePercentage: 10,
      veteranOwnedPreferenceEnabled: true,
      veteranOwnedPreferencePercentage: 5,
      awardCriteria: {
        price: 40,
        technicalCapability: 30,
        pastPerformance: 20,
        deliveryTime: 10
      }
    },
    agencyManagement: {
      multiTierApprovalRequired: true,
      approvalTiers: 3,
      agencyTypes: ['Federal', 'State', 'Local', 'Military'],
      contractingOfficerApprovalRequired: true,
      legalReviewRequired: true,
      legalReviewThreshold: 100000,
      budgetOfficerApprovalRequired: true,
      chiefApprovalThreshold: 500000
    },
    contractTemplates: {
      useStandardTemplates: true,
      allowCustomClauses: false,
      requiredClauses: ['FAR 52.212-4', 'FAR 52.212-5', 'Equal Opportunity'],
      forbiddenClauses: ['Arbitration', 'Non-Compete'],
      templateApprovalRequired: true,
      contractTypeDefaults: {
        fixedPrice: true,
        costReimbursement: false,
        timeAndMaterials: true,
        indefiniteDelivery: false
      }
    },
    complianceAudit: {
      auditTrailEnabled: true,
      detailedLoggingEnabled: true,
      retentionPeriodYears: 7,
      quarterlyReportsRequired: true,
      annualAuditsRequired: true,
      externalAuditorsAllowed: false,
      farComplianceChecks: true,
      whistleblowerProtection: true,
      fraudDetectionEnabled: true
    },
    securityAccess: {
      clearanceLevels: ['Public', 'Confidential', 'Secret'],
      documentClassificationEnabled: true,
      encryptionRequired: true,
      vpnRequired: false,
      ipRestrictionEnabled: false,
      allowedIpRanges: [],
      twoFactorRequired: true,
      sessionTimeoutMinutes: 30,
      maxConcurrentSessions: 2,
      geoRestrictionEnabled: false,
      allowedCountries: ['US']
    },
    lastModified: new Date().toISOString(),
    lastModifiedBy: 'admin@evoltbay.com'
  })

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)
  const [authError, setAuthError] = useState(false)

  useEffect(() => {
    loadSettings()
  }, [])

  const loadSettings = async () => {
    try {
      setLoading(true)
      const response = await adminSettingsApi.getSettings('government')
      if (response.success) {
        setSettings(response.data)
      } else {
        console.warn('API returned success=false:', response)
        // Keep default settings on API error
      }
    } catch (error: any) {
      console.error('Failed to load government settings:', error)

      // Handle authentication errors
      if (error.response?.status === 401 || error.response?.status === 403) {
        console.warn('Authentication error - user may not be logged in as admin')
        setAuthError(true)
      }

      // Keep default settings on error so UI doesn't crash
    } finally {
      setLoading(false)
    }
  }

  const handleSettingChange = (path: string, value: any) => {
    setSettings(prev => {
      const newSettings = { ...prev }
      const keys = path.split('.')
      let current: any = newSettings

      for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]]
      }
      current[keys[keys.length - 1]] = value

      return newSettings
    })
    setHasChanges(true)
  }

  const handleArrayToggle = (path: string, value: string) => {
    setSettings(prev => {
      const newSettings = { ...prev }
      const keys = path.split('.')
      let current: any = newSettings

      for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]]
      }

      const array = current[keys[keys.length - 1]]
      if (array.includes(value)) {
        current[keys[keys.length - 1]] = array.filter((item: string) => item !== value)
      } else {
        current[keys[keys.length - 1]] = [...array, value]
      }

      return newSettings
    })
    setHasChanges(true)
  }

  const handleArrayChange = (path: string, value: string[]) => {
    setSettings(prev => {
      const newSettings = { ...prev }
      const keys = path.split('.')
      let current: any = newSettings

      for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]]
      }
      current[keys[keys.length - 1]] = value

      return newSettings
    })
    setHasChanges(true)
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      const response = await adminSettingsApi.updateSettings('government', settings)
      if (response.success) {
        setSettings(response.data)
        setHasChanges(false)
        toast.success('Government settings saved successfully!')
      }
    } catch (error) {
      console.error('Failed to save government settings:', error)
      toast.error('Failed to save government settings')
    } finally {
      setSaving(false)
    }
  }

  const handleReset = async () => {
    try {
      setSaving(true)
      const response = await adminSettingsApi.resetSettings('government')
      if (response.success) {
        setSettings(response.data)
        setHasChanges(false)
        toast.success('Government settings reset to defaults!')
      }
    } catch (error) {
      console.error('Failed to reset government settings:', error)
      toast.error('Failed to reset government settings')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="py-12 text-center">
        <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-600" />
        <p className="text-gray-600">Loading government settings...</p>
      </div>
    )
  }

  if (authError) {
    return (
      <div className="py-12 text-center">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 max-w-md mx-auto">
          <div className="flex items-center justify-center mb-4">
            <AlertTriangle className="h-12 w-12 text-yellow-600" />
          </div>
          <h3 className="text-lg font-medium text-yellow-800 mb-2">
            Authentication Required
          </h3>
          <p className="text-yellow-700 mb-4">
            You need to be logged in as an admin to access these settings.
          </p>
          <p className="text-sm text-yellow-600">
            Please log in with admin credentials and try again.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center space-x-2 sm:space-x-3">
          <Landmark className="h-5 w-5 sm:h-6 sm:w-6 text-indigo-600 flex-shrink-0" />
          <div>
            <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">Government Contracting System</h2>
            <p className="text-gray-600 text-sm sm:text-base">Configure procurement rules, vendor verification, compliance, and government workflows</p>
          </div>
        </div>

        {/* Save/Reset Actions */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
          <button
            onClick={handleReset}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={!hasChanges}
          >
            Reset
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className="flex items-center justify-center space-x-2 px-4 sm:px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
          >
            {saving ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            <span className="text-sm sm:text-base">{saving ? 'Saving...' : 'Save Changes'}</span>
          </button>
        </div>
      </div>

      {/* Last Modified Info */}
      <div className="flex items-center space-x-2 p-3 bg-gray-50 rounded-lg">
        <CheckCircle className="h-4 w-4 text-green-600" />
        <span className="text-sm text-gray-600">
          Last modified: {new Date(settings.lastModified).toLocaleString()} by {settings.lastModifiedBy}
        </span>
      </div>

      {/* Settings Sections */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 sm:gap-8">
        {/* General Settings */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Landmark className="h-5 w-5 mr-2 text-blue-600" />
            General Settings
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Workflow Mode</label>
              <select
                value={settings.general.workflowMode}
                onChange={(e) => handleSettingChange('general.workflowMode', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="open">Open Competition</option>
                <option value="restricted">Restricted Competition</option>
                <option value="sole-source">Sole Source</option>
                <option value="sealed">Sealed Bidding</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Default Posting Window (Days)</label>
              <input
                type="number"
                min="1"
                max="365"
                value={settings.general.defaultPostingWindowDays}
                onChange={(e) => handleSettingChange('general.defaultPostingWindowDays', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Minimum Bid Window (Days)</label>
              <input
                type="number"
                min="1"
                max="180"
                value={settings.general.minimumBidWindowDays}
                onChange={(e) => handleSettingChange('general.minimumBidWindowDays', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Procurement Threshold ($)</label>
              <input
                type="number"
                min="0"
                step="1000"
                value={settings.general.procurementThreshold}
                onChange={(e) => handleSettingChange('general.procurementThreshold', parseFloat(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">Minimum amount requiring formal procurement</p>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">Allow Emergency Procurement</label>
                <p className="text-xs text-gray-500">Fast-track urgent requirements</p>
              </div>
              <input
                type="checkbox"
                checked={settings.general.allowEmergencyProcurement}
                onChange={(e) => handleSettingChange('general.allowEmergencyProcurement', e.target.checked)}
                className="h-4 w-4 text-blue-600 rounded"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">Public Notice Required</label>
                <p className="text-xs text-gray-500">Publish RFQs to public portals</p>
              </div>
              <input
                type="checkbox"
                checked={settings.general.publicNoticeRequired}
                onChange={(e) => handleSettingChange('general.publicNoticeRequired', e.target.checked)}
                className="h-4 w-4 text-blue-600 rounded"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">Bid Bond Required</label>
                <p className="text-xs text-gray-500">Require bid security deposit</p>
              </div>
              <input
                type="checkbox"
                checked={settings.general.bidBondRequired}
                onChange={(e) => handleSettingChange('general.bidBondRequired', e.target.checked)}
                className="h-4 w-4 text-blue-600 rounded"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">Performance Bond Required</label>
                <p className="text-xs text-gray-500">Require performance security</p>
              </div>
              <input
                type="checkbox"
                checked={settings.general.performanceBondRequired}
                onChange={(e) => handleSettingChange('general.performanceBondRequired', e.target.checked)}
                className="h-4 w-4 text-blue-600 rounded"
              />
            </div>
          </div>
        </div>

        {/* Vendor Verification */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <FileCheck className="h-5 w-5 mr-2 text-green-600" />
            Vendor Verification
          </h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">Certification Required</label>
                <p className="text-xs text-gray-500">Vendors must be certified to bid</p>
              </div>
              <input
                type="checkbox"
                checked={settings.vendorVerification.certificationRequired}
                onChange={(e) => handleSettingChange('vendorVerification.certificationRequired', e.target.checked)}
                className="h-4 w-4 text-green-600 rounded"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Required Documents</label>
              <div className="space-y-2">
                {['company_registration', 'tax_id', 'insurance_certificate', 'financial_statements', 'references', 'certifications'].map(doc => (
                  <label key={doc} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={settings.vendorVerification.requiredDocuments.includes(doc)}
                      onChange={() => handleArrayToggle('vendorVerification.requiredDocuments', doc)}
                      className="h-4 w-4 text-green-600 rounded"
                    />
                    <span className="text-sm capitalize">{doc.replace(/_/g, ' ')}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">Auto-Verification Enabled</label>
                <p className="text-xs text-gray-500">Automatically verify standard documents</p>
              </div>
              <input
                type="checkbox"
                checked={settings.vendorVerification.autoVerificationEnabled}
                onChange={(e) => handleSettingChange('vendorVerification.autoVerificationEnabled', e.target.checked)}
                className="h-4 w-4 text-green-600 rounded"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Verification Timeout (Days)</label>
              <input
                type="number"
                min="1"
                max="30"
                value={settings.vendorVerification.verificationTimeoutDays}
                onChange={(e) => handleSettingChange('vendorVerification.verificationTimeoutDays', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              />
            </div>

            <div className="border-t pt-4">
              <h4 className="text-sm font-medium text-gray-700 mb-3">Government Registrations</h4>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-gray-700">SAM Registration Required</label>
                    <p className="text-xs text-gray-500">System for Award Management</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.vendorVerification.samRegistrationRequired}
                    onChange={(e) => handleSettingChange('vendorVerification.samRegistrationRequired', e.target.checked)}
                    className="h-4 w-4 text-green-600 rounded"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-gray-700">CAGE Code Required</label>
                    <p className="text-xs text-gray-500">Commercial and Government Entity</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.vendorVerification.cageCodeRequired}
                    onChange={(e) => handleSettingChange('vendorVerification.cageCodeRequired', e.target.checked)}
                    className="h-4 w-4 text-green-600 rounded"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-gray-700">DUNS Number Required</label>
                    <p className="text-xs text-gray-500">Data Universal Numbering System</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.vendorVerification.dunsNumberRequired}
                    onChange={(e) => handleSettingChange('vendorVerification.dunsNumberRequired', e.target.checked)}
                    className="h-4 w-4 text-green-600 rounded"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-gray-700">GSA Schedule Preferred</label>
                    <p className="text-xs text-gray-500">General Services Administration</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.vendorVerification.gsaSchedulePreferred}
                    onChange={(e) => handleSettingChange('vendorVerification.gsaSchedulePreferred', e.target.checked)}
                    className="h-4 w-4 text-green-600 rounded"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Security Clearance Required</label>
                    <p className="text-xs text-gray-500">For classified contracts</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.vendorVerification.securityClearanceRequired}
                    onChange={(e) => handleSettingChange('vendorVerification.securityClearanceRequired', e.target.checked)}
                    className="h-4 w-4 text-green-600 rounded"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Procurement Rules */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Scale className="h-5 w-5 mr-2 text-purple-600" />
            Procurement Rules
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Bid Opening Policy</label>
              <select
                value={settings.procurementRules.bidOpeningPolicy}
                onChange={(e) => handleSettingChange('procurementRules.bidOpeningPolicy', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              >
                <option value="immediate">Immediate</option>
                <option value="scheduled">Scheduled Public Opening</option>
                <option value="private">Private Opening</option>
                <option value="two-envelope">Two-Envelope System</option>
              </select>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">Allow Seated Bids</label>
                <p className="text-xs text-gray-500">In-person bid submission</p>
              </div>
              <input
                type="checkbox"
                checked={settings.procurementRules.allowSeatedBids}
                onChange={(e) => handleSettingChange('procurementRules.allowSeatedBids', e.target.checked)}
                className="h-4 w-4 text-purple-600 rounded"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">Allow Electronic Bids</label>
                <p className="text-xs text-gray-500">Online bid submission</p>
              </div>
              <input
                type="checkbox"
                checked={settings.procurementRules.allowElectronicBids}
                onChange={(e) => handleSettingChange('procurementRules.allowElectronicBids', e.target.checked)}
                className="h-4 w-4 text-purple-600 rounded"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Tie-Breaking Method</label>
              <select
                value={settings.procurementRules.tieBreakingMethod}
                onChange={(e) => handleSettingChange('procurementRules.tieBreakingMethod', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              >
                <option value="lowest-price">Lowest Price</option>
                <option value="local-preference">Local Preference</option>
                <option value="small-business">Small Business Preference</option>
                <option value="coin-toss">Random Selection</option>
                <option value="negotiation">Negotiation</option>
              </select>
            </div>

            <div className="border-t pt-4">
              <h4 className="text-sm font-medium text-gray-700 mb-3">Vendor Preferences</h4>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Local Preference Enabled</label>
                    <p className="text-xs text-gray-500">Favor local vendors</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.procurementRules.localPreferenceEnabled}
                    onChange={(e) => handleSettingChange('procurementRules.localPreferenceEnabled', e.target.checked)}
                    className="h-4 w-4 text-purple-600 rounded"
                  />
                </div>

                {settings.procurementRules.localPreferenceEnabled && (
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Local Preference (%)</label>
                    <input
                      type="number"
                      min="0"
                      max="25"
                      value={settings.procurementRules.localPreferencePercentage}
                      onChange={(e) => handleSettingChange('procurementRules.localPreferencePercentage', parseFloat(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Small Business Preference</label>
                    <p className="text-xs text-gray-500">Favor small businesses</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.procurementRules.smallBusinessPreferenceEnabled}
                    onChange={(e) => handleSettingChange('procurementRules.smallBusinessPreferenceEnabled', e.target.checked)}
                    className="h-4 w-4 text-purple-600 rounded"
                  />
                </div>

                {settings.procurementRules.smallBusinessPreferenceEnabled && (
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Small Business Preference (%)</label>
                    <input
                      type="number"
                      min="0"
                      max="25"
                      value={settings.procurementRules.smallBusinessPreferencePercentage}
                      onChange={(e) => handleSettingChange('procurementRules.smallBusinessPreferencePercentage', parseFloat(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Veteran-Owned Preference</label>
                    <p className="text-xs text-gray-500">Favor veteran-owned businesses</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.procurementRules.veteranOwnedPreferenceEnabled}
                    onChange={(e) => handleSettingChange('procurementRules.veteranOwnedPreferenceEnabled', e.target.checked)}
                    className="h-4 w-4 text-purple-600 rounded"
                  />
                </div>

                {settings.procurementRules.veteranOwnedPreferenceEnabled && (
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Veteran-Owned Preference (%)</label>
                    <input
                      type="number"
                      min="0"
                      max="25"
                      value={settings.procurementRules.veteranOwnedPreferencePercentage}
                      onChange={(e) => handleSettingChange('procurementRules.veteranOwnedPreferencePercentage', parseFloat(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="border-t pt-4">
              <h4 className="text-sm font-medium text-gray-700 mb-3">Award Criteria Weighting (%)</h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Price</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={settings.procurementRules.awardCriteria.price}
                    onChange={(e) => handleSettingChange('procurementRules.awardCriteria.price', parseFloat(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Technical Capability</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={settings.procurementRules.awardCriteria.technicalCapability}
                    onChange={(e) => handleSettingChange('procurementRules.awardCriteria.technicalCapability', parseFloat(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Past Performance</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={settings.procurementRules.awardCriteria.pastPerformance}
                    onChange={(e) => handleSettingChange('procurementRules.awardCriteria.pastPerformance', parseFloat(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Delivery Time</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={settings.procurementRules.awardCriteria.deliveryTime}
                    onChange={(e) => handleSettingChange('procurementRules.awardCriteria.deliveryTime', parseFloat(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">Total should equal 100%</p>
            </div>
          </div>
        </div>

        {/* Agency Management */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Building2 className="h-5 w-5 mr-2 text-orange-600" />
            Agency Management
          </h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">Multi-Tier Approval Required</label>
                <p className="text-xs text-gray-500">Multiple approval levels for contracts</p>
              </div>
              <input
                type="checkbox"
                checked={settings.agencyManagement.multiTierApprovalRequired}
                onChange={(e) => handleSettingChange('agencyManagement.multiTierApprovalRequired', e.target.checked)}
                className="h-4 w-4 text-orange-600 rounded"
              />
            </div>

            {settings.agencyManagement.multiTierApprovalRequired && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Approval Tiers</label>
                <input
                  type="number"
                  min="1"
                  max="5"
                  value={settings.agencyManagement.approvalTiers}
                  onChange={(e) => handleSettingChange('agencyManagement.approvalTiers', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Supported Agency Types</label>
              <div className="space-y-2">
                {['Federal', 'State', 'Local', 'Military', 'Educational', 'Healthcare'].map(type => (
                  <label key={type} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={settings.agencyManagement.agencyTypes.includes(type)}
                      onChange={() => handleArrayToggle('agencyManagement.agencyTypes', type)}
                      className="h-4 w-4 text-orange-600 rounded"
                    />
                    <span className="text-sm">{type}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="border-t pt-4">
              <h4 className="text-sm font-medium text-gray-700 mb-3">Approval Requirements</h4>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Contracting Officer Approval</label>
                    <p className="text-xs text-gray-500">CO must approve all contracts</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.agencyManagement.contractingOfficerApprovalRequired}
                    onChange={(e) => handleSettingChange('agencyManagement.contractingOfficerApprovalRequired', e.target.checked)}
                    className="h-4 w-4 text-orange-600 rounded"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Legal Review Required</label>
                    <p className="text-xs text-gray-500">Legal counsel review for contracts</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.agencyManagement.legalReviewRequired}
                    onChange={(e) => handleSettingChange('agencyManagement.legalReviewRequired', e.target.checked)}
                    className="h-4 w-4 text-orange-600 rounded"
                  />
                </div>

                {settings.agencyManagement.legalReviewRequired && (
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Legal Review Threshold ($)</label>
                    <input
                      type="number"
                      min="0"
                      step="10000"
                      value={settings.agencyManagement.legalReviewThreshold}
                      onChange={(e) => handleSettingChange('agencyManagement.legalReviewThreshold', parseFloat(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                    />
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Budget Officer Approval</label>
                    <p className="text-xs text-gray-500">Budget approval for all contracts</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.agencyManagement.budgetOfficerApprovalRequired}
                    onChange={(e) => handleSettingChange('agencyManagement.budgetOfficerApprovalRequired', e.target.checked)}
                    className="h-4 w-4 text-orange-600 rounded"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Chief Approval Threshold ($)</label>
                  <input
                    type="number"
                    min="0"
                    step="50000"
                    value={settings.agencyManagement.chiefApprovalThreshold}
                    onChange={(e) => handleSettingChange('agencyManagement.chiefApprovalThreshold', parseFloat(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Requires agency chief approval above this amount</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Contract Templates */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <FileSignature className="h-5 w-5 mr-2 text-red-600" />
            Contract Templates
          </h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">Use Standard Templates</label>
                <p className="text-xs text-gray-500">Enforce standard contract templates</p>
              </div>
              <input
                type="checkbox"
                checked={settings.contractTemplates.useStandardTemplates}
                onChange={(e) => handleSettingChange('contractTemplates.useStandardTemplates', e.target.checked)}
                className="h-4 w-4 text-red-600 rounded"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">Allow Custom Clauses</label>
                <p className="text-xs text-gray-500">Allow agency-specific clauses</p>
              </div>
              <input
                type="checkbox"
                checked={settings.contractTemplates.allowCustomClauses}
                onChange={(e) => handleSettingChange('contractTemplates.allowCustomClauses', e.target.checked)}
                className="h-4 w-4 text-red-600 rounded"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Required Clauses</label>
              <div className="space-y-2">
                {['FAR 52.212-4', 'FAR 52.212-5', 'Equal Opportunity', 'Buy American', 'Davis-Bacon', 'Service Contract Act'].map(clause => (
                  <label key={clause} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={settings.contractTemplates.requiredClauses.includes(clause)}
                      onChange={() => handleArrayToggle('contractTemplates.requiredClauses', clause)}
                      className="h-4 w-4 text-red-600 rounded"
                    />
                    <span className="text-sm">{clause}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Forbidden Clauses</label>
              <div className="space-y-2">
                {['Arbitration', 'Non-Compete', 'No-Bid', 'Automatic Renewal', 'Penalty Waivers'].map(clause => (
                  <label key={clause} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={settings.contractTemplates.forbiddenClauses.includes(clause)}
                      onChange={() => handleArrayToggle('contractTemplates.forbiddenClauses', clause)}
                      className="h-4 w-4 text-red-600 rounded"
                    />
                    <span className="text-sm">{clause}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">Template Approval Required</label>
                <p className="text-xs text-gray-500">Legal must approve new templates</p>
              </div>
              <input
                type="checkbox"
                checked={settings.contractTemplates.templateApprovalRequired}
                onChange={(e) => handleSettingChange('contractTemplates.templateApprovalRequired', e.target.checked)}
                className="h-4 w-4 text-red-600 rounded"
              />
            </div>

            <div className="border-t pt-4">
              <h4 className="text-sm font-medium text-gray-700 mb-3">Contract Type Defaults</h4>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm">Fixed Price</span>
                  <input
                    type="checkbox"
                    checked={settings.contractTemplates.contractTypeDefaults.fixedPrice}
                    onChange={(e) => handleSettingChange('contractTemplates.contractTypeDefaults.fixedPrice', e.target.checked)}
                    className="h-4 w-4 text-red-600 rounded"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">Cost Reimbursement</span>
                  <input
                    type="checkbox"
                    checked={settings.contractTemplates.contractTypeDefaults.costReimbursement}
                    onChange={(e) => handleSettingChange('contractTemplates.contractTypeDefaults.costReimbursement', e.target.checked)}
                    className="h-4 w-4 text-red-600 rounded"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">Time & Materials</span>
                  <input
                    type="checkbox"
                    checked={settings.contractTemplates.contractTypeDefaults.timeAndMaterials}
                    onChange={(e) => handleSettingChange('contractTemplates.contractTypeDefaults.timeAndMaterials', e.target.checked)}
                    className="h-4 w-4 text-red-600 rounded"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">Indefinite Delivery</span>
                  <input
                    type="checkbox"
                    checked={settings.contractTemplates.contractTypeDefaults.indefiniteDelivery}
                    onChange={(e) => handleSettingChange('contractTemplates.contractTypeDefaults.indefiniteDelivery', e.target.checked)}
                    className="h-4 w-4 text-red-600 rounded"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Compliance & Audit */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Shield className="h-5 w-5 mr-2 text-yellow-600" />
            Compliance & Audit
          </h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">Audit Trail Enabled</label>
                <p className="text-xs text-gray-500">Log all procurement actions</p>
              </div>
              <input
                type="checkbox"
                checked={settings.complianceAudit.auditTrailEnabled}
                onChange={(e) => handleSettingChange('complianceAudit.auditTrailEnabled', e.target.checked)}
                className="h-4 w-4 text-yellow-600 rounded"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">Detailed Logging Enabled</label>
                <p className="text-xs text-gray-500">Capture detailed transaction logs</p>
              </div>
              <input
                type="checkbox"
                checked={settings.complianceAudit.detailedLoggingEnabled}
                onChange={(e) => handleSettingChange('complianceAudit.detailedLoggingEnabled', e.target.checked)}
                className="h-4 w-4 text-yellow-600 rounded"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Retention Period (Years)</label>
              <input
                type="number"
                min="1"
                max="50"
                value={settings.complianceAudit.retentionPeriodYears}
                onChange={(e) => handleSettingChange('complianceAudit.retentionPeriodYears', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">Quarterly Reports Required</label>
                <p className="text-xs text-gray-500">Generate quarterly procurement reports</p>
              </div>
              <input
                type="checkbox"
                checked={settings.complianceAudit.quarterlyReportsRequired}
                onChange={(e) => handleSettingChange('complianceAudit.quarterlyReportsRequired', e.target.checked)}
                className="h-4 w-4 text-yellow-600 rounded"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">Annual Audits Required</label>
                <p className="text-xs text-gray-500">Conduct annual procurement audits</p>
              </div>
              <input
                type="checkbox"
                checked={settings.complianceAudit.annualAuditsRequired}
                onChange={(e) => handleSettingChange('complianceAudit.annualAuditsRequired', e.target.checked)}
                className="h-4 w-4 text-yellow-600 rounded"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">External Auditors Allowed</label>
                <p className="text-xs text-gray-500">Allow third-party audit access</p>
              </div>
              <input
                type="checkbox"
                checked={settings.complianceAudit.externalAuditorsAllowed}
                onChange={(e) => handleSettingChange('complianceAudit.externalAuditorsAllowed', e.target.checked)}
                className="h-4 w-4 text-yellow-600 rounded"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">FAR Compliance Checks</label>
                <p className="text-xs text-gray-500">Federal Acquisition Regulation checks</p>
              </div>
              <input
                type="checkbox"
                checked={settings.complianceAudit.farComplianceChecks}
                onChange={(e) => handleSettingChange('complianceAudit.farComplianceChecks', e.target.checked)}
                className="h-4 w-4 text-yellow-600 rounded"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">Whistleblower Protection</label>
                <p className="text-xs text-gray-500">Enable anonymous reporting</p>
              </div>
              <input
                type="checkbox"
                checked={settings.complianceAudit.whistleblowerProtection}
                onChange={(e) => handleSettingChange('complianceAudit.whistleblowerProtection', e.target.checked)}
                className="h-4 w-4 text-yellow-600 rounded"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">Fraud Detection Enabled</label>
                <p className="text-xs text-gray-500">AI-powered fraud detection</p>
              </div>
              <input
                type="checkbox"
                checked={settings.complianceAudit.fraudDetectionEnabled}
                onChange={(e) => handleSettingChange('complianceAudit.fraudDetectionEnabled', e.target.checked)}
                className="h-4 w-4 text-yellow-600 rounded"
              />
            </div>
          </div>
        </div>

        {/* Security & Access */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Lock className="h-5 w-5 mr-2 text-gray-700" />
            Security & Access
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Clearance Levels</label>
              <div className="space-y-2">
                {['Public', 'Confidential', 'Secret', 'Top Secret'].map(level => (
                  <label key={level} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={settings.securityAccess.clearanceLevels.includes(level)}
                      onChange={() => handleArrayToggle('securityAccess.clearanceLevels', level)}
                      className="h-4 w-4 text-gray-700 rounded"
                    />
                    <span className="text-sm">{level}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">Document Classification Enabled</label>
                <p className="text-xs text-gray-500">Classify documents by sensitivity</p>
              </div>
              <input
                type="checkbox"
                checked={settings.securityAccess.documentClassificationEnabled}
                onChange={(e) => handleSettingChange('securityAccess.documentClassificationEnabled', e.target.checked)}
                className="h-4 w-4 text-gray-700 rounded"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">Encryption Required</label>
                <p className="text-xs text-gray-500">Encrypt all documents at rest</p>
              </div>
              <input
                type="checkbox"
                checked={settings.securityAccess.encryptionRequired}
                onChange={(e) => handleSettingChange('securityAccess.encryptionRequired', e.target.checked)}
                className="h-4 w-4 text-gray-700 rounded"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">VPN Required</label>
                <p className="text-xs text-gray-500">Require VPN for system access</p>
              </div>
              <input
                type="checkbox"
                checked={settings.securityAccess.vpnRequired}
                onChange={(e) => handleSettingChange('securityAccess.vpnRequired', e.target.checked)}
                className="h-4 w-4 text-gray-700 rounded"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">IP Restriction Enabled</label>
                <p className="text-xs text-gray-500">Restrict access to specific IPs</p>
              </div>
              <input
                type="checkbox"
                checked={settings.securityAccess.ipRestrictionEnabled}
                onChange={(e) => handleSettingChange('securityAccess.ipRestrictionEnabled', e.target.checked)}
                className="h-4 w-4 text-gray-700 rounded"
              />
            </div>

            {settings.securityAccess.ipRestrictionEnabled && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Allowed IP Ranges</label>
                <textarea
                  rows={3}
                  placeholder="Enter IP ranges, one per line (e.g., 192.168.1.0/24)"
                  value={settings.securityAccess.allowedIpRanges.join('\n')}
                  onChange={(e) => handleArrayChange('securityAccess.allowedIpRanges', e.target.value.split('\n').filter(ip => ip.trim()))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 font-mono text-sm"
                />
              </div>
            )}

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">Two-Factor Required</label>
                <p className="text-xs text-gray-500">Require 2FA for all users</p>
              </div>
              <input
                type="checkbox"
                checked={settings.securityAccess.twoFactorRequired}
                onChange={(e) => handleSettingChange('securityAccess.twoFactorRequired', e.target.checked)}
                className="h-4 w-4 text-gray-700 rounded"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Session Timeout (Minutes)</label>
              <input
                type="number"
                min="5"
                max="480"
                value={settings.securityAccess.sessionTimeoutMinutes}
                onChange={(e) => handleSettingChange('securityAccess.sessionTimeoutMinutes', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Max Concurrent Sessions</label>
              <input
                type="number"
                min="1"
                max="10"
                value={settings.securityAccess.maxConcurrentSessions}
                onChange={(e) => handleSettingChange('securityAccess.maxConcurrentSessions', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">Geo-Restriction Enabled</label>
                <p className="text-xs text-gray-500">Restrict access by country</p>
              </div>
              <input
                type="checkbox"
                checked={settings.securityAccess.geoRestrictionEnabled}
                onChange={(e) => handleSettingChange('securityAccess.geoRestrictionEnabled', e.target.checked)}
                className="h-4 w-4 text-gray-700 rounded"
              />
            </div>

            {settings.securityAccess.geoRestrictionEnabled && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Allowed Countries</label>
                <div className="space-y-2">
                  {['US', 'CA', 'UK', 'AU', 'NZ'].map(country => (
                    <label key={country} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={settings.securityAccess.allowedCountries.includes(country)}
                        onChange={() => handleArrayToggle('securityAccess.allowedCountries', country)}
                        className="h-4 w-4 text-gray-700 rounded"
                      />
                      <span className="text-sm">{country}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Warning Banner */}
      {hasChanges && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <AlertTriangle className="h-5 w-5 text-yellow-600" />
            <div>
              <p className="text-yellow-800 font-medium">Unsaved Changes</p>
              <p className="text-yellow-700 text-sm">You have unsaved changes. Click "Save Changes" to apply them.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default GovernmentSettingsPanel
